<template>
    <QrWrapper v-if="loaded">
        <div class="h-full flex flex-col max-w-xl mx-auto">
            <div class="flex mt-24">
                <LogoImage full class="mx-auto h-16 mb-4"/>
            </div>

            <Message
                v-if="!registerKey"
                alert
                :header="trans('message.you-can-only-register-through-an-agency')"
                header-trans="message.you-can-only-register-through-an-agency"
            />
            <Message
                v-else-if="registerKey.is_used"
                alert
                :header="trans('message.this-signup-key-is-already-used')"
                header-trans="message.this-signup-key-is-already-used"
            />
            <QrForm v-else @submit.prevent="register" class="space-y-4 px-6 py-5 sm:p-6 mt-2">
                <QrInput v-model="name" :label="trans('input.name')" :errors="validationErrors.name"/>
                <QrInput v-model="email" :label="trans('input.email')" type="email" :errors="validationErrors.email"/>
                <QrSelect
                    v-model="country"
                    :label="trans('input.country')"
                    :options="[
                        { text: trans('option.norway'), value: 'norway' },
                        { text: trans('option.sweden'), value: 'sweden' },
                        { text: trans('option.england'), value: 'england' },
                    ]"
                />
                <QrInput v-model="password" :label="trans('input.password')" type="password" :errors="validationErrors.password"/>
                <QrInput v-model="passwordConfirmation" :label="trans('input.confirm-password')" type="password" :errors="validationErrors.password_confirmation"/>

                <QrButton
                    type="submit"
                    :loading="loading"
                    :recently-failed="recentlyFailed"
                    :recently-succeeded="recentlySucceeded"
                    :text="trans('button.register')" trans="button.register"
                    class="w-full"
                    icon-right="arrow-right"
                />
            </QrForm>

            <p class="mt-6 text-sm text-center text-gray-500">
                <QrParagraph :text="trans('text.already-have-an-account?')" trans="already-have-an-account?"/>
                <RouterLink to="/login" class="font-semibold leading-6 text-indigo-600 hover:text-indigo-500">
                    {{ trans('button.login') }}
                    <QrIcon icon="arrow-right"/>
                    <TranslateText text="button.login"/>
                </RouterLink>
            </p>
        </div>
    </QrWrapper>
</template>

<script setup>
import QrInput from "@/components/application-ui/form/QrInput.vue";
import {onBeforeMount, onMounted, ref} from "vue";
import { trans } from "@/libs/helper.js";
import { useUserStore } from "@/store/user.js";
import { useStatesStore } from "@/store/states.js";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import QrForm from "@/components/application-ui/content/QrForm.vue";
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import TranslateText from "@/components/TranslateText.vue";
import QrParagraph from "@/components/application-ui/content/QrParagraph.vue";
import QrWrapper from "@/components/application-ui/panels/QrWrapper.vue";
import QrSelect from "@/components/application-ui/form/QrSelect.vue";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import LogoImage from "@/components/LogoImage.vue";
import {useRegisterStore} from "@/store/register.js";
import Message from "@/components/application-ui/feedback/Message.vue";

const userStore = useUserStore();
const satesStore = useStatesStore();
const registerStore = useRegisterStore();

const route = useRoute();
const router = useRouter();

const loaded = ref(false);
const name = ref('');
const email = ref('');
const country = ref('norway');
const password = ref('');
const passwordConfirmation = ref('');
const { loading, recentlySucceeded, recentlyFailed, validationErrors } = storeToRefs(satesStore);
const { user } = storeToRefs(userStore);
const { registerKey } = storeToRefs(registerStore);

const register = async () => {
    const payload = {
        name: name.value,
        email: email.value,
        country: country.value,
        password: password.value,
        password_confirmation: passwordConfirmation.value,
        key: route.query['agency-key']
    };

    await userStore.register(payload, async () => {
        await axios.post('/api/login', {
            email: email.value,
            password: password.value,
            remember: true,
        });

        await userStore.fetch();
        await router.push({ name: 'dashboard' });
    });
};

const setAgency = async () => {
    if (! route.query['agency-key']) {
        return;
    }

    await registerStore.fetch(route.query['agency-key'])
};

onBeforeMount(async () => await setAgency())

onMounted(async () => {
    if (user.value) {
        if (route.query.redirect === '/') {
            await router.push('/dashboard');
        }

        await router.push(route.query.redirect || '/dashboard');
    }

    loaded.value = true;
});
</script>
